import { useC6Bank } from "@/app/contexts/C6BankContext";
import { c6BankService } from "@/app/services/c6BankService";
import { currentStep } from "@/app/store";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import masks from "@/app/utils/masks";
import { Button } from "@/view/components/ui/button";
import { useToast } from "@/view/components/ui/use-toast";
import { useAtom } from "jotai";
import { useState } from "react";

interface ReviewProposalProps {
  visible?: boolean;
}

export default function ReviewProposal({ visible }: ReviewProposalProps) {
  const [, setStep] = useAtom(currentStep);

  const { toast } = useToast();

  const { proposalData, customerData, setProposalData } = useC6Bank();

  const [isLoading, setIsLoading] = useState(false);

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";

    const [year, month, day] = dateString.split("-");

    return `${day}/${month}/${year}`;
  };

  const formatAccountType = (type?: string) => {
    switch (type) {
      case "CP":
        return "Poupança";
      case "CC":
        return "Conta corrente";
      default:
        return "-";
    }
  };

  const personalFields = [
    { label: "Nome", value: customerData?.name || "-" },
    { label: "CPF", value: masks.cpf(customerData?.cpf || "") },
    { label: "RG", value: masks.rg(customerData?.rg?.number || "") },
    {
      label: "Data de emissão",
      value: customerData?.rg?.date
        ? formatDate(customerData.rg.date)
        : "-",
    },
  ];

  const bankFields = [
    {
      label: "Tipo de conta",
      value: formatAccountType(customerData?.bankAccount?.accountType),
    },
    { label: "Banco", value: customerData?.bankAccount?.bank || "-" },
    { label: "Agência", value: customerData?.bankAccount?.agency || "-" },
    { label: "Conta", value: customerData?.bankAccount?.account || "-" },
  ];

  const handleCreateProposal = async () => {
    if (
      !proposalData?.idSimulation ||
      !proposalData?.value ||
      !proposalData?.prazo ||
      !proposalData?.customerId
    ) {
      return;
    }

    try {
      setIsLoading(true);

      const response = await c6BankService.createTransaction({
        id_simulacao: proposalData.idSimulation,
        value: proposalData.value,
        Prazo: proposalData.prazo,
        customerId: proposalData.customerId,
      });

      setProposalData({
        contract: response.contract,
      });

      setStep((state) => state + 1);
    } catch (error: any) {
      toast({
        title: "Erro ao criar transação",
        description:
          error?.message ||
          error?.data?.message ||
          error?.response?.data?.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={
        visible ? "mt-8 flex flex-col gap-8 max-sm:mt-4 max-sm:gap-4" : "hidden"
      }
    >
      <div className="text-black01">
        <p className="text-sm font-medium">Valor proposto</p>
        <h3 className="text-3xl font-semibold">
          {addMoneySimbols(Math.round(Number(proposalData?.value)))}
        </h3>
      </div>
      <div className="flex max-h-[260px] flex-col gap-4 overflow-y-auto border-b pb-4 max-sm:max-h-[200px]">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <p className="text-sm font-semibold text-black01">Dados pessoais</p>
            <div className="h-px flex-1 bg-gray01" />
          </div>
          <ul className="flex flex-col">
            {personalFields.map(({ label, value }) => (
              <li
                className="flex items-baseline justify-between font-medium max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:py-1"
                key={label}
              >
                <p className="text-xs text-black04">{label}</p>
                <strong className="text-sm text-black01 max-sm:text-xs">
                  {value}
                </strong>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-3">
            <p className="text-sm font-semibold text-black01">Dados bancários</p>
            <div className="h-px flex-1 bg-gray01" />
          </div>
          <ul className="flex flex-col">
            {bankFields.map(({ label, value }) => (
              <li
                className="flex items-baseline justify-between font-medium max-sm:flex-col max-sm:items-start max-sm:gap-1 max-sm:py-1"
                key={label}
              >
                <p className="text-xs text-black04">{label}</p>
                <strong className="text-sm text-black01 max-sm:text-xs">
                  {value}
                </strong>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex justify-end gap-5 max-sm:flex-col max-sm:gap-3">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-black01 hover:bg-white02 max-sm:order-2"
          onClick={() => setStep(0)}
        >
          Editar dados
        </Button>
        <Button
          type="button"
          className="rounded-full px-8 py-3 font-semibold text-white max-sm:order-1"
          onClick={handleCreateProposal}
          disabled={isLoading}
          isLoading={isLoading}
        >
          Realizar proposta
        </Button>
      </div>
    </div>
  );
}
